import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import type React from 'react';
import { twMerge } from 'tailwind-merge';
import { Text } from './text';

const sizes = {
  xs: 'tw:sm:max-w-xs',
  sm: 'tw:sm:max-w-sm',
  md: 'tw:sm:max-w-md',
  lg: 'tw:sm:max-w-lg',
  xl: 'tw:sm:max-w-xl',
  '2xl': 'tw:sm:max-w-2xl',
  '3xl': 'tw:sm:max-w-3xl',
  '4xl': 'tw:sm:max-w-4xl',
  '5xl': 'tw:sm:max-w-5xl',
};

export function Dialog({
  size = 'lg',
  align = 'center',
  className,
  children,
  ...props
}: {
  size?: keyof typeof sizes;
  align?: 'center' | 'top';
  className?: string;
  children: React.ReactNode;
} & Omit<Headless.DialogProps, 'as' | 'className'>) {
  return (
    <Headless.Dialog {...props}>
      <Headless.DialogBackdrop
        transition
        className="tw:fixed tw:inset-0 tw:flex tw:w-screen tw:justify-center tw:overflow-y-auto tw:bg-zinc-950/25 tw:px-2 tw:py-2 tw:transition tw:duration-100 tw:focus:outline-0 tw:data-[closed]:opacity-0 tw:data-[enter]:ease-out tw:data-[leave]:ease-in tw:sm:px-6 tw:sm:py-8 tw:lg:px-8 tw:lg:py-16 tw:dark:bg-zinc-950/50 tw:z-[60]"
      />

      <div
        className={clsx(
          'tw:fixed tw:inset-0 tw:w-screen tw:overflow-y-auto tw:p-4 tw:grid tw:min-h-full tw:sm:p-4 tw:z-[60]',
          align === 'top'
            ? 'tw:items-start tw:justify-center tw:pt-4 tw:sm:pt-8'
            : 'tw:place-items-center tw:sm:grid-rows-[1fr_auto_3fr]'
        )}
      >
        <Headless.DialogPanel
          transition
          className={twMerge(
            clsx(
              sizes[size],
              'tw:w-full tw:min-w-0 tw:rounded-2xl tw:bg-white tw:p-6 tw:shadow-lg tw:ring-1 tw:ring-zinc-950/10 tw:[--gutter:theme(spacing.8)] tw:sm:mb-auto tw:sm:rounded-2xl tw:dark:bg-zinc-900 tw:dark:ring-white/10 tw:forced-colors:outline tw:z-[60]',
              'tw:transition tw:duration-100 tw:will-change-transform tw:data-[closed]:translate-y-12 tw:data-[closed]:opacity-0 tw:data-[enter]:ease-out tw:data-[leave]:ease-in tw:sm:data-[closed]:translate-y-0 tw:sm:data-[closed]:data-[enter]:scale-95',
              className
            )
          )}
        >
          {children}
        </Headless.DialogPanel>
      </div>
    </Headless.Dialog>
  );
}

type DialogCloseButtonProps = {
  className?: string;
  'aria-label'?: string;
  onClick: () => void;
};

export function DialogCloseButton({
  className,
  'aria-label': ariaLabel,
  onClick,
}: DialogCloseButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel || 'Close dialog'}
      className={twMerge(
        clsx(
          'tw:rounded-md tw:p-1.5 tw:text-zinc-400 tw:transition tw:hover:bg-zinc-100 tw:hover:text-zinc-500 tw:dark:hover:bg-zinc-800 tw:dark:hover:text-zinc-300',
          className
        )
      )}
    >
      <svg
        className="tw:h-6 tw:w-6"
        fill="none"
        viewBox="0 0 24 24"
        strokeWidth="1.5"
        stroke="currentColor"
        aria-hidden="true"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
      </svg>
    </button>
  );
}

export function DialogTitle({
  className,
  ...props
}: { className?: string } & Omit<Headless.DialogTitleProps, 'as' | 'className'>) {
  return (
    <Headless.DialogTitle
      {...props}
      className={clsx(
        className,
        'tw:text-balance tw:text-lg/6 tw:font-semibold tw:text-zinc-950 tw:sm:text-base/6 tw:dark:!text-white'
      )}
    />
  );
}

export function DialogDescription({
  className,
  ...props
}: { className?: string } & Omit<Headless.DescriptionProps<typeof Text>, 'as' | 'className'>) {
  return (
    <Headless.Description
      as={Text}
      {...props}
      className={clsx(className, 'tw:mt-2 tw:text-pretty')}
    />
  );
}

type DialogBodyProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DialogBody({ className, ...props }: DialogBodyProps) {
  return <div {...props} className={twMerge(clsx('tw:mt-6', className))} />;
}

type DialogActionsProps = {
  className?: string;
} & React.ComponentPropsWithoutRef<'div'>;

export function DialogActions({ className, ...props }: DialogActionsProps) {
  return (
    <div
      {...props}
      className={twMerge(
        clsx(
          'tw:mt-8 tw:flex tw:flex-col-reverse tw:items-center tw:justify-end tw:gap-3 tw:*:w-full tw:sm:flex-row tw:sm:*:w-auto',
          className
        )
      )}
    />
  );
}
